import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { RoleBadge } from "@/components/RoleBadge";
import { Ribbon } from "lucide-react";
import { toast } from "sonner";

// Public role badge request (2026-02). Fixed role list, two evidence fields.
// Factual copy only — the badge shows the role label, nothing more.
export const RoleBadgeSection = () => {
  const [data, setData] = useState(null); // {badge, request, roles}
  const [role, setRole] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.myRoleBadge().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const pending = data.request?.status === "pending";

  const submit = async () => {
    if (!role) return toast.error("Pick your role");
    setBusy(true);
    try {
      await api.requestRoleBadge(role, workEmail.trim(), link.trim());
      toast.success("Request sent — our team will review it");
      setRole(""); setWorkEmail(""); setLink("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="role-badge-section" className="pt-4 border-t border-neutral-100">
      <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
        <Ribbon className="w-3.5 h-3.5 text-indigo-500" /> Public role badge
      </label>
      {data.badge ? (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="role-badge-current">
          <RoleBadge badge={data.badge} />
          <span className="text-xs text-neutral-500">shows next to your name across CivicHearth.</span>
        </div>
      ) : pending ? (
        <p data-testid="role-badge-pending" className="text-xs text-neutral-500 mt-1.5">
          Your "{data.request.role_label}" badge request is being reviewed.
        </p>
      ) : (
        <>
          <p className="text-xs text-neutral-500 mt-1.5">
            Hold a civic role? Request a small badge showing it next to your name.
            We check against your official work email (a public page link helps too) — nothing else is stored.
          </p>
          {data.request?.status === "declined" && (
            <p data-testid="role-badge-declined" className="text-xs text-amber-700 mt-1.5">
              Your last request wasn't approved{data.request.note ? ` — "${data.request.note}"` : ""}. You can request again.
            </p>
          )}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="role-badge-role-select" className="h-10">
                <SelectValue placeholder="Your role" />
              </SelectTrigger>
              <SelectContent>
                {data.roles.map((r) => <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="role-badge-email-input" type="email" value={workEmail}
              onChange={(e) => setWorkEmail(e.target.value)}
              placeholder="Official work email" className="h-10" />
            <Input data-testid="role-badge-link-input" value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Link to your official page (optional)" className="h-10" />
          </div>
          <Button data-testid="role-badge-submit" onClick={submit} disabled={busy}
            variant="outline" className="rounded-full mt-2 h-9 text-sm">
            Request badge
          </Button>
        </>
      )}
    </div>
  );
};
